import { BranchesOutlined, CalendarOutlined, CheckCircleOutlined, CloudSyncOutlined, DeleteOutlined, DownloadOutlined, DownOutlined, ExclamationCircleOutlined, EyeOutlined, FormOutlined, GlobalOutlined, LinkOutlined, MoreOutlined, OrderedListOutlined, PauseCircleOutlined, UpOutlined, WarningFilled } from '@ant-design/icons';
import { Button, Card, Col, Divider, Dropdown, Empty, List, Pagination, Row, Space, Tooltip, Typography } from 'antd';
import moment from 'moment';
import React, { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import { Modal } from '../../components/Modal';
import { OriginInformationProgress } from '../../components/OriginInformationProgress';
import { ACTION, FEATURE } from '../../config/roles';
import { useAuth } from '../../hooks/auth';
import { Api_v2 } from '../../services/api';
import { WorkflowStatus } from '../../services/api/workflow';

const { Text, Title } = Typography;

const STATUS_COLORS = {
    Pending: 'rgb(199 200 202)',
    Open: '#4085c6',
    Late: '#ff4d4f',
    Done: '#34d399',
    Approval: '#60a5fa',
};

// Helper to extract workflow key
function getWorkflowKey(status) {
    return (
        Object.entries(WorkflowStatus).find(([, v]) => v === status)?.[0] || ''
    );
}

// Download PDF report
function downloadReport(correlationId) {
    Api_v2.Workflow.PdfReport(correlationId)
        .then(({ data }) => {
            const blob = new Blob([data], { type: 'application/pdf' });
            const url = window.URL.createObjectURL(blob);
            const date = moment().format('YYYYMMDD');
            const a = document.createElement('a');
            a.href = url;
            a.download = `Workflow_${correlationId}_Report_${date}.pdf`;
            a.click();
        })
        .catch(console.error);
}

export const TaskHistoryTableBody = ({
    listAnswersdata,
    setStoreFilters,
    handleEditSchedule,
    handleDeleteAnswer,
    handleReOpenAnswer,
    handleCloseTask,
    paginationAnswersConfig,
    onChangeAnswersTablePagination,
    tasksDone,
    tasksPending,
    tasksOpen,
    tasksLate,
    workflowRunning,
    showOriginInformationProgress,
    formId,
    handleStatus,
}) => {
    const [expandedId, setExpandedId] = useState(null);
    const [modalVisible, setModalVisible] = useState(false);
    const [modalContent, setModalContent] = useState(null);
    const { t } = useTranslation();
    const { can } = useAuth();

    const navigate = useNavigate();

    moment.locale('en');

    if (!listAnswersdata) return null;

    // Stats cards
    const stats = useMemo(
        () => [
            {
                key: 'running',
                label: t('History.WorkflowRunning'),
                value: workflowRunning,
                icon: <BranchesOutlined style={{ color: STATUS_COLORS.Open, fontSize: 28, marginRight: 20 }} />,
                onClick: () => handleStatus('Running'),
            },
            {
                key: 'pending',
                label: t('History.PendingTasks'),
                value: tasksPending,
                icon: <PauseCircleOutlined style={{ color: STATUS_COLORS.Open, fontSize: 28, marginRight: 20 }} />,
                onClick: () => handleStatus('Pending'),
            },
            {
                key: 'late',
                label: t('History.LateTasks'),
                value: tasksLate,
                icon: <ExclamationCircleOutlined style={{ color: STATUS_COLORS.Late, fontSize: 28, marginRight: 20 }} />,
                onClick: () => handleStatus('Late'),
            },
            {
                key: 'open',
                label: t('History.OpenTasks'),
                value: tasksOpen,
                icon: <CloudSyncOutlined style={{ color: STATUS_COLORS.Open, fontSize: 28, marginRight: 20 }} />,
                onClick: () => handleStatus('Open'),
            },
            {
                key: 'done',
                label: t('History.DoneTasks'),
                value: tasksDone,
                icon: <CheckCircleOutlined style={{ color: STATUS_COLORS.Done, fontSize: 28, marginRight: 20 }} />,
                onClick: () => handleStatus('Done'),
            },
            {
                key: 'total',
                label: t('TableTotal'),
                value: tasksDone + tasksLate + tasksOpen + tasksPending,
                icon: <OrderedListOutlined style={{ color: STATUS_COLORS.Open, fontSize: 28, marginRight: 20 }} />,
                onClick: null,
            },
        ],
        [t, workflowRunning, tasksPending, tasksLate, tasksOpen, tasksDone, handleStatus]
    );

    // Build action menu
    const buildMenuItems = useCallback(
        (item) => {
            const items = [];
            if (can(FEATURE.SCHEDULE, [ACTION.EDIT])) {
                items.push({ key: 'export', label: t('OpExportTask'), icon: <FormOutlined />, onClick: () => handleEditSchedule(item) });
                if (item.status !== 'Done') {
                    items.push({ key: 'edit', label: t('Schedule.Edit.Schedule'), icon: <FormOutlined />, onClick: () => handleEditSchedule(item) });
                }
            }
            if (can(FEATURE.FORMTASK, [ACTION.CLOSETASK]) && item.reOpen === 1 && item.status !== 'Done') {
                items.push({ key: 'close', label: t('BtnCloseTask'), icon: <GlobalOutlined />, onClick: () => handleCloseTask(item) });
            }
            if (can(FEATURE.FORMTASK, [ACTION.DELETE])) {
                if (item.correlationId) {
                    items.push({ key: 'delRestart', label: t('BtnDeleteRestart'), icon: <DeleteOutlined />, onClick: () => handleDeleteAnswer(item, true, false) });
                    items.push({ key: 'delAll', label: t('BtnDeleteAll'), icon: <DeleteOutlined />, onClick: () => handleDeleteAnswer(item, false, true) });
                    items.push({ key: 'report', label: t('BtnWorkflowReport'), icon: <DownloadOutlined />, onClick: () => downloadReport(item.correlationId) });
                } else {
                    items.push({ key: 'delete', label: t('BtnDelete'), icon: <DeleteOutlined />, onClick: () => handleDeleteAnswer(item, false, false) });
                    items.push({
                        key: 'workflow', label: t('Workflow'), icon: <FormOutlined />, onClick: () => {
                            setModalContent(
                                <List
                                    header={<Title level={4}>{item.title}</Title>}
                                    itemLayout="vertical"
                                    size="small"
                                    dataSource={item.correlatedWorkflowInstances}
                                    renderItem={(w) => (
                                        <List.Item>
                                            <Title level={5}>
                                                {w.name} <Link onClick={setStoreFilters} to={`/workflow/detail/${w.instanceId}`}><LinkOutlined /></Link>
                                            </Title>
                                            <Space direction="vertical">
                                                <Text><Text strong>Created Date:</Text> {w.startedDate?.toLocaleString()}</Text>
                                                <Text><Text strong>Status:</Text> {getWorkflowKey(w.status)}</Text>
                                            </Space>
                                        </List.Item>
                                    )}
                                />
                            );
                            setModalVisible(true);
                        }
                    });
                }
            }
            if (can(FEATURE.FORMTASK, [ACTION.REOPEN]) && item.status === 'Done') {
                items.push({ key: 'reopen', label: t('BtnReopen'), icon: <GlobalOutlined />, onClick: () => handleReOpenAnswer(item) });
            }
            return items;
        },
        [can, t, handleEditSchedule, handleDeleteAnswer, handleCloseTask, handleReOpenAnswer, setStoreFilters]
    );

    // Render stats
    const renderStats = () => (
        <Row gutter={8} style={{ marginBottom: 16 }}>
            {stats.map(({ key, label, value, icon, onClick }) => (
                <Col span={4} key={key}>
                    <Tooltip title={onClick ? `${t('BtnFilter')} ${label}` : ''}>
                        <Card
                            hoverable={!!onClick}
                            onClick={onClick}
                            style={{
                                cursor: onClick ? 'pointer' : undefined,
                                height: "100%",
                                minHeight: 100,
                                display: "flex",
                                alignItems: "center",
                            }}
                        >
                            <div style={{ display: "flex", alignItems: "center", width: "100%" }}>
                                {icon}
                                <div style={{ flex: 1, minWidth: 0 }}>
                                    <Text
                                        style={{
                                            marginBottom: 4,
                                            whiteSpace: 'normal',
                                            wordBreak: 'break-word',
                                        }}
                                    >
                                        {label}
                                    </Text>
                                    <Title
                                        level={4}
                                        style={{
                                            marginTop: 0,
                                            marginBottom: 4,
                                            whiteSpace: 'normal',
                                            wordBreak: 'break-word',
                                        }}
                                    >
                                        {value}
                                    </Title>
                                </div>
                            </div>
                        </Card>
                    </Tooltip>
                </Col>
            ))}
        </Row>
    );

    const renderList = () =>
        listAnswersdata.length === 0 ? (
            <Empty style={{ marginTop: 40 }} />
        ) : (
            listAnswersdata.map((resp, idx) => {
                const statusColor = STATUS_COLORS[resp.status] || STATUS_COLORS.Pending;
                const workflowItems = resp.workflowTasks || [];
                const isExpanded = expandedId === resp.id;
                const menuItems = buildMenuItems(resp);

                return (
                    <React.Fragment key={resp.id}>
                        <Card bodyStyle={{ padding: 0 }} className={idx == 0 ? '' : 'mt-10'} style={{ overflow: 'hidden', marginBottom: 0 }}>
                            <div style={{ background: '#fff', padding: 12, borderLeft: `4px solid ${statusColor}` }}>
                                <Row justify="space-between" align="middle">
                                    <Col>
                                        <Space size="middle">
                                            <div style={{ backgroundColor: statusColor, color: '#fff', fontWeight: 500, borderRadius: 8, padding: '2px 8px', fontSize: 12, display: 'inline-block' }}>
                                                {t(resp.status)}
                                            </div>
                                            {resp.approvalPending && resp.status == "Approval" && <div style={{ backgroundColor: "#ffaa00", color: '#fff', fontWeight: 500, borderRadius: 8, padding: '2px 8px', fontSize: 12, display: 'inline-block' }}>
                                                <WarningFilled /> {t("Home.WaitingApproval")}
                                            </div>}
                                            <span>
                                                <small>{t('Code')}</small>: <strong>{resp.friendlyId ?? resp.id}</strong>
                                            </span>
                                            {resp.correlationId && (
                                                <span>
                                                    <small>Workflow</small>: <strong>{resp.correlationId}</strong>
                                                </span>
                                            )}
                                            {workflowItems.length > 0 && (
                                                <Button
                                                    type="text"
                                                    size="small"
                                                    icon={isExpanded ? <UpOutlined /> : <DownOutlined />}
                                                    onClick={() => setExpandedId(isExpanded ? null : resp.id)}
                                                >
                                                    {t('Workflow')}
                                                </Button>
                                            )}
                                            <Button
                                                type="text"
                                                size="small"
                                                icon={<EyeOutlined />}
                                                onClick={() =>
                                                    navigate(
                                                        resp.status === 'Done' || resp.status === 'Approval'
                                                            ? `/formbuilder/view-answer/${resp.id}`
                                                            : `/formbuilder/apply/${resp.formId}/${resp.id}`
                                                    )
                                                }
                                            >
                                                {t('Visualizar')}
                                            </Button>
                                        </Space>
                                    </Col>
                                    <Col>
                                        {can(FEATURE.FORMTASK, [ACTION.DELETE, ACTION.REOPEN, ACTION.EDIT, ACTION.CLOSETASK]) && (
                                            <Dropdown
                                                menu={{ items: menuItems }}
                                                trigger={["click"]}
                                                placement="bottomLeft"
                                            >
                                                <MoreOutlined
                                                    style={{ float: 'right', fontSize: 18, paddingRight: 5, cursor: 'pointer' }}
                                                    onClick={e => e.stopPropagation()}
                                                />
                                            </Dropdown>
                                        )}
                                    </Col>
                                </Row>
                                <Divider style={{ margin: '10px 0px 5px 0px' }} />
                                <Row gutter={[8, 8]}>
                                    <Col span={4}>
                                        <Text type="secondary" style={{ fontSize: 12 }}>{t('ConfigPanel.Form.FormName')}</Text>
                                        <br />
                                        <Text strong>{resp.name}</Text>
                                    </Col>
                                    <Col span={6}>
                                        <Text type="secondary" style={{ fontSize: 12 }}>{t('Description')}</Text>
                                        <br />
                                        <Text strong>{resp.description || resp.name}</Text>
                                    </Col>
                                    <Col span={1}>
                                        <Text type="secondary" style={{ fontSize: 12 }}>{t('Version')}</Text>
                                        <br />
                                        <Text strong>{resp.formVersion}</Text>
                                    </Col>
                                    <Col span={3}>
                                        <Text type="secondary" style={{ fontSize: 12 }}>{t('Location')}</Text>
                                        <br />
                                        <Text strong>{resp.locationName}</Text>
                                    </Col>
                                    <Col span={2}>
                                        <Text type="secondary" style={{ fontSize: 12 }}>{t('CreatedBy')}</Text>
                                        <br />
                                        <Text strong>{resp.insertUserName}</Text>
                                    </Col>
                                    <Col span={3}>
                                        <Text type="secondary" style={{ fontSize: 12 }}>
                                            <CalendarOutlined style={{ marginRight: 4 }} />
                                            {t('CreatedAt')}
                                        </Text>
                                        <br />
                                        <Text strong>{moment(resp.insertDate).format('YYYY-MMM-DD HH:mm')}</Text>
                                    </Col>
                                    <Col span={2}>
                                        <Text type="secondary" style={{ fontSize: 12 }}>{t('AssignedTo')}</Text>
                                        <br />
                                        <Text strong>{resp.userName}</Text>
                                    </Col>
                                    <Col span={3}>
                                        <Text type="secondary" style={{ fontSize: 12 }}>
                                            <CalendarOutlined style={{ marginRight: 4 }} />
                                            {t('DueDate')}
                                        </Text>
                                        <br />
                                        <Text strong>{moment(resp.scheduleDate).format('YYYY-MMM-DD HH:mm')}</Text>
                                    </Col>
                                </Row>
                            </div>

                            {isExpanded && (
                                <>
                                    <Divider style={{ margin: 0 }} />
                                    <div style={{ padding: 16, background: '#fff' }}>
                                        <Title level={5}>{t('Menu.Workflow')}</Title>
                                        {workflowItems.map((item, idx) => {
                                            const itemColor = STATUS_COLORS[item.status] || STATUS_COLORS.Pending;
                                            return (
                                                <React.Fragment key={idx}>
                                                    <Card
                                                        bodyStyle={{ padding: 0 }}
                                                        style={{ background: '#f9fafb', padding: 12, borderLeft: `4px solid ${itemColor}`, marginBottom: 12 }}
                                                    >
                                                        <Row justify="space-between" align="middle" gutter={16}>
                                                            <Col>
                                                                <Space size="middle">
                                                                    <div style={{ backgroundColor: itemColor, color: '#fff', fontWeight: 500, borderRadius: 8, padding: '2px 8px', fontSize: 12, display: 'inline-block' }}>
                                                                        {t(item.status)}
                                                                    </div>
                                                                    <span>
                                                                        <small>{t('Code')}</small>: <strong>{item.friendlyId ?? item.id}</strong>
                                                                    </span>
                                                                    {item.correlationId && (
                                                                        <span>
                                                                            <small>Workflow</small>: <strong>{item.correlationId}</strong>
                                                                        </span>
                                                                    )}
                                                                    <Button
                                                                        type="text"
                                                                        size="small"
                                                                        icon={<EyeOutlined />}
                                                                        onClick={() =>
                                                                            window.open(
                                                                                resp.status === 'Done'
                                                                                    ? `/formbuilder/view-answer/${resp.id}`
                                                                                    : `/formbuilder/apply/${formId.id}/${resp.id}`
                                                                            )
                                                                        }
                                                                    >
                                                                        {t('Visualizar')}
                                                                    </Button>
                                                                </Space>
                                                            </Col>
                                                        </Row>
                                                        <Row gutter={[8, 8]} style={{ marginTop: 12 }}>
                                                            <Col span={4}>
                                                                <Text type="secondary" style={{ fontSize: 12 }}>{t('ConfigPanel.Form.FormName')}</Text>
                                                                <br />
                                                                <Text strong>{item.name}</Text>
                                                            </Col>
                                                            <Col span={6}>
                                                                <Text type="secondary" style={{ fontSize: 12 }}>{t('Description')}</Text>
                                                                <br />
                                                                <Text strong>{item.description || item.name}</Text>
                                                            </Col>
                                                            <Col span={1}>
                                                                <Text type="secondary" style={{ fontSize: 12 }}>{t('Version')}</Text>
                                                                <br />
                                                                <Text strong>{item.formVersion}</Text>
                                                            </Col>
                                                            <Col span={2}>
                                                                <Text type="secondary" style={{ fontSize: 12 }}>{t('Location')}</Text>
                                                                <br />
                                                                <Text strong>{item.locationName}</Text>
                                                            </Col>
                                                            <Col span={2}>
                                                                <Text type="secondary" style={{ fontSize: 12 }}>{t('CreatedBy')}</Text>
                                                                <br />
                                                                <Text strong>{item.insertUserName}</Text>
                                                            </Col>
                                                            <Col span={3}>
                                                                <Text type="secondary" style={{ fontSize: 12 }}>
                                                                    <CalendarOutlined style={{ marginRight: 4 }} />
                                                                    {t('CreatedAt')}
                                                                </Text>
                                                                <br />
                                                                <Text strong>{moment(item.insertDate).format('YYYY-MMM-DD HH:mm')}</Text>
                                                            </Col>
                                                            <Col span={2}>
                                                                <Text type="secondary" style={{ fontSize: 12 }}>{t('AssignedTo')}</Text>
                                                                <br />
                                                                <Text strong>{item.userName}</Text>
                                                            </Col>
                                                            <Col span={3}>
                                                                <Text type="secondary" style={{ fontSize: 12 }}>
                                                                    <CalendarOutlined style={{ marginRight: 4 }} />
                                                                    {t('DueDate')}
                                                                </Text>
                                                                <br />
                                                                <Text strong>{moment(item.scheduleDate).format('YYYY-MMM-DD HH:mm')}</Text>
                                                            </Col>
                                                        </Row>
                                                    </Card>
                                                </React.Fragment>
                                            );
                                        })}
                                    </div>
                                </>
                            )}
                        </Card>
                        {
                            idx === listAnswersdata.length - 1 && (
                                <Row justify="end" gutter={[24, 24]} style={{ padding: 10 }}>
                                    <Col>
                                        <Pagination
                                            current={paginationAnswersConfig.current}
                                            total={paginationAnswersConfig.total}
                                            showQuickJumper
                                            onChange={onChangeAnswersTablePagination}
                                            disabled={!paginationAnswersConfig.total}
                                        />
                                    </Col>
                                </Row>
                            )
                        }
                    </React.Fragment >
                );
            })
        );

    return (
        <>
            {renderStats()}
            {showOriginInformationProgress && (
                <Row>
                    <Col span={24}>
                        <Card style={{ marginBottom: 15 }}>
                            <OriginInformationProgress formId={formId} />
                        </Card>
                    </Col>
                </Row>
            )}
            {renderList()}
            <Modal open={modalVisible} onCancel={() => setModalVisible(false)} footer={null}>
                {modalContent}
            </Modal>
        </>
    );
};
